// ====================================================================
// CLIENT FOLLOW-UP CRM - STAFF WORKFLOW & DASHBOARD VIEW
// ====================================================================

import { getClients, getInteractions, getClientByMobile, addClient, addInteraction } from '../store.js';
import { getCurrentUser } from '../auth.js';
import { showToast, openModal, closeModal, escapeHtml, formatDate } from '../ui.js';

export async function renderStaffView(container) {
  const clients = await getClients();
  const interactions = await getInteractions();
  const todayStr = new Date().toISOString().split('T')[0];

  const todayFollowups = interactions.filter(i => i.next_followup_date === todayStr && i.status !== 'Won' && i.status !== 'Lost' && i.status !== 'Closed');
  const upcomingFollowups = interactions.filter(i => i.next_followup_date > todayStr && i.status !== 'Won' && i.status !== 'Lost' && i.status !== 'Closed');
  const overdueFollowups = interactions.filter(i => i.next_followup_date && i.next_followup_date < todayStr && i.status !== 'Won' && i.status !== 'Lost' && i.status !== 'Closed');

  container.innerHTML = `
    <div class="page-header">
      <div>
        <h1 class="page-title">Staff Portal & Daily Follow-ups</h1>
        <p class="page-subtitle">Search clients, log interactions, and stay on top of follow-ups</p>
      </div>
      <button id="btn-quick-client" class="btn btn-primary">
        <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
        Create Client
      </button>
    </div>

    <!-- Quick Client Lookup Box -->
    <div class="card" style="background: linear-gradient(135deg, var(--bg-card), var(--primary-light)); border: 1px solid var(--primary-light);">
      <h3 style="font-size: 1.1rem; font-weight: 700; margin-bottom: 0.5rem; color: var(--text-primary);">
        ⚡ 15-Second Client Lookup & Interaction Logging
      </h3>
      <p style="font-size: 0.875rem; color: var(--text-secondary); margin-bottom: 1rem;">
        Enter client mobile number or name to quickly log a call or meeting:
      </p>

      <div style="display: flex; gap: 0.75rem; max-width: 600px;">
        <input type="text" id="staff-search-input" class="form-control" placeholder="Enter Mobile Number or Client Name..." style="flex: 1;">
        <button id="btn-staff-search-go" class="btn btn-primary">
          Search Client
        </button>
      </div>
      <div id="staff-search-results" style="margin-top: 1rem;"></div>
    </div>

    <!-- Quick Follow-up Stats -->
    <div class="kpi-grid">
      <div class="kpi-card overdue">
        <div class="kpi-header">
          <span class="kpi-title">Overdue Follow-ups</span>
          <div class="kpi-icon">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
          </div>
        </div>
        <div class="kpi-value" style="color: var(--color-overdue);">${overdueFollowups.length}</div>
      </div>

      <div class="kpi-card today">
        <div class="kpi-header">
          <span class="kpi-title">Today's Follow-ups</span>
          <div class="kpi-icon">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </div>
        </div>
        <div class="kpi-value" style="color: var(--color-today);">${todayFollowups.length}</div>
      </div>

      <div class="kpi-card upcoming">
        <div class="kpi-header">
          <span class="kpi-title">Upcoming Follow-ups</span>
          <div class="kpi-icon">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
          </div>
        </div>
        <div class="kpi-value" style="color: var(--color-upcoming);">${upcomingFollowups.length}</div>
      </div>
    </div>

    <!-- Follow-up Tables -->
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(400px, 1fr)); gap: 1.5rem;">
      <!-- Today's Queue -->
      <div class="card" style="border-top: 4px solid var(--color-today);">
        <div class="card-header">
          <h3 class="card-title">Today's Follow-up Queue</h3>
          <span class="badge badge-today">${todayFollowups.length} Due</span>
        </div>
        <div class="table-responsive">
          <table class="table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Company</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              ${todayFollowups.length === 0 ? '<tr><td colspan="4" style="text-align:center; color:var(--text-muted);">No follow-ups due today. Great work!</td></tr>' :
                todayFollowups.map(i => `
                  <tr>
                    <td><strong>${escapeHtml(i.client?.client_name || 'Client')}</strong><br><span style="font-size:0.75rem; color:var(--text-muted);">${escapeHtml(i.client?.mobile_number || '')}</span></td>
                    <td>${escapeHtml(i.client?.company || '-')}</td>
                    <td><span class="badge badge-today">${escapeHtml(i.status)}</span></td>
                    <td><a href="#profile?id=${i.client_id}" class="btn btn-primary btn-sm">Follow Up</a></td>
                  </tr>
                `).join('')
              }
            </tbody>
          </table>
        </div>
      </div>

      <!-- Upcoming Queue -->
      <div class="card" style="border-top: 4px solid var(--color-upcoming);">
        <div class="card-header">
          <h3 class="card-title">Upcoming Follow-ups</h3>
          <span class="badge badge-upcoming">${upcomingFollowups.length} Scheduled</span>
        </div>
        <div class="table-responsive">
          <table class="table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Next Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              ${upcomingFollowups.length === 0 ? '<tr><td colspan="4" style="text-align:center; color:var(--text-muted);">No upcoming follow-ups.</td></tr>' :
                upcomingFollowups.map(i => `
                  <tr>
                    <td><strong>${escapeHtml(i.client?.client_name || 'Client')}</strong><br><span style="font-size:0.75rem; color:var(--text-muted);">${escapeHtml(i.client?.mobile_number || '')}</span></td>
                    <td><span class="badge badge-upcoming">${formatDate(i.next_followup_date)}</span></td>
                    <td><span class="badge badge-warm">${escapeHtml(i.status)}</span></td>
                    <td><a href="#profile?id=${i.client_id}" class="btn btn-secondary btn-sm">View Profile</a></td>
                  </tr>
                `).join('')
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `;

  // Attach search listeners
  const searchInput = document.getElementById('staff-search-input');
  const resultsDiv = document.getElementById('staff-search-results');

  const executeStaffSearch = async () => {
    const query = searchInput.value.trim().toLowerCase();
    if (!query) {
      resultsDiv.innerHTML = '';
      return;
    }

    const matched = clients.filter(c =>
      c.client_name.toLowerCase().includes(query) ||
      (c.mobile_number && c.mobile_number.includes(query))
    );

    if (matched.length === 0) {
      resultsDiv.innerHTML = `
        <div style="padding: 1rem; background-color: var(--bg-app); border-radius: var(--radius-md); border: 1px dashed var(--border-color); display: flex; align-items: center; justify-content: space-between;">
          <span style="color: var(--text-muted);">No existing client found for "${escapeHtml(query)}".</span>
          <button id="btn-create-missing-client" class="btn btn-primary btn-sm">
            Create Client "${escapeHtml(query)}"
          </button>
        </div>
      `;
      document.getElementById('btn-create-missing-client')?.addEventListener('click', () => {
        openStaffCreateClientModal(query);
      });
    } else {
      resultsDiv.innerHTML = matched.map(c => `
        <div style="padding: 0.875rem 1rem; background-color: var(--bg-card); border-radius: var(--radius-md); border: 1px solid var(--border-color); margin-bottom: 0.5rem; display: flex; align-items: center; justify-content: space-between;">
          <div>
            <strong style="color: var(--text-primary);">${escapeHtml(c.client_name)}</strong>
            <span style="margin-left: 0.5rem; font-size: 0.8rem; color: var(--text-muted);">${escapeHtml(c.mobile_number)} &bull; ${escapeHtml(c.company || 'No Company')}</span>
          </div>
          <div style="display: flex; gap: 0.5rem;">
            <a href="#profile?id=${c.id}" class="btn btn-secondary btn-sm">Profile</a>
            <button class="btn btn-primary btn-sm btn-quick-add-int" data-id="${c.id}" data-name="${escapeHtml(c.client_name)}">
              + Add Interaction
            </button>
          </div>
        </div>
      `).join('');

      document.querySelectorAll('.btn-quick-add-int').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const clientId = e.currentTarget.getAttribute('data-id');
          const clientName = e.currentTarget.getAttribute('data-name');
          openAddInteractionModal(clientId, clientName);
        });
      });
    }
  };

  document.getElementById('btn-staff-search-go').addEventListener('click', executeStaffSearch);
  searchInput.addEventListener('keyup', (e) => {
    if (e.key === 'Enter') executeStaffSearch();
  });

  document.getElementById('btn-quick-client').addEventListener('click', () => {
    openStaffCreateClientModal('');
  });
}

export function openStaffCreateClientModal(prefillValue = '') {
  const isMobile = /^\+?\d+$/.test(prefillValue.replace(/[\s-]/g, ''));
  const prefillName = isMobile ? '' : prefillValue;
  const prefillMobile = isMobile ? prefillValue : '';

  const modalBody = document.getElementById('modal-client-body');
  document.getElementById('modal-client-title').textContent = 'Create New Client';

  modalBody.innerHTML = `
    <form id="staff-client-form">
      <div class="form-group">
        <label class="form-label">Client Name <span class="required">*</span></label>
        <input type="text" id="m-client-name" class="form-control" placeholder="e.g. John Smith" required value="${escapeHtml(prefillName)}">
      </div>
      <div class="form-group">
        <label class="form-label">Mobile Number <span class="required">*</span></label>
        <input type="tel" id="m-client-mobile" class="form-control" placeholder="e.g. +1 9876543210" required value="${escapeHtml(prefillMobile)}">
      </div>
      <div class="form-group">
        <label class="form-label">Company</label>
        <input type="text" id="m-client-company" class="form-control" placeholder="e.g. Acme Corp">
      </div>
      <div class="form-group">
        <label class="form-label">Category <span class="required">*</span></label>
        <select id="m-client-category" class="form-select" required>
          <option value="Hot">Hot Lead</option>
          <option value="Warm" selected>Warm Lead</option>
          <option value="Cold">Cold Lead</option>
          <option value="Closed">Closed</option>
        </select>
      </div>
      <div class="modal-footer" style="padding: 1rem 0 0 0; margin-top: 1rem;">
        <button type="button" class="btn btn-secondary" onclick="document.getElementById('modal-client').classList.remove('active')">Cancel</button>
        <button type="submit" class="btn btn-primary">Save & Add Interaction →</button>
      </div>
    </form>
  `;

  openModal('modal-client');

  document.getElementById('staff-client-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const currentUser = getCurrentUser();

    const newClientData = {
      client_name: document.getElementById('m-client-name').value.trim(),
      mobile_number: document.getElementById('m-client-mobile').value.trim(),
      company: document.getElementById('m-client-company').value.trim(),
      category: document.getElementById('m-client-category').value,
      owner_id: currentUser ? currentUser.id : null
    };

    try {
      const created = await addClient(newClientData);
      showToast(`Client "${created.client_name}" created successfully!`, 'success');
      closeModal('modal-client');
      // Immediately redirect to Add Interaction modal!
      openAddInteractionModal(created.id, created.client_name);
    } catch (err) {
      showToast(err.message || 'Failed to create client', 'error');
    }
  });
}

export function openAddInteractionModal(clientId, clientName) {
  const modalBody = document.getElementById('modal-interaction-body');
  document.getElementById('modal-interaction-title').textContent = `Add Interaction for ${clientName}`;

  const todayStr = new Date().toISOString().split('T')[0];

  modalBody.innerHTML = `
    <form id="interaction-form">
      <div class="form-group">
        <label class="form-label">Interaction Date <span class="required">*</span></label>
        <input type="date" id="int-date" class="form-control" required value="${todayStr}">
      </div>

      <div class="form-group">
        <label class="form-label">Status <span class="required">*</span></label>
        <select id="int-status" class="form-select" required>
          <option value="New Lead">New Lead</option>
          <option value="Called" selected>Called</option>
          <option value="Interested">Interested</option>
          <option value="Follow-up">Follow-up</option>
          <option value="Meeting Scheduled">Meeting Scheduled</option>
          <option value="Quotation Sent">Quotation Sent</option>
          <option value="Negotiation">Negotiation</option>
          <option value="Won">Won</option>
          <option value="Lost">Lost</option>
          <option value="Closed">Closed</option>
        </select>
      </div>

      <div class="form-group">
        <label class="form-label">Next Follow-up Date</label>
        <input type="date" id="int-next-date" class="form-control">
      </div>

      <div class="form-group">
        <label class="form-label">Interaction Notes</label>
        <textarea id="int-notes" class="form-control" rows="3" placeholder="Summary of discussion, key takeaways..."></textarea>
      </div>

      <div class="modal-footer" style="padding: 1rem 0 0 0; margin-top: 1rem;">
        <button type="button" class="btn btn-secondary" onclick="document.getElementById('modal-interaction').classList.remove('active')">Cancel</button>
        <button type="submit" class="btn btn-primary">Save Interaction</button>
      </div>
    </form>
  `;

  openModal('modal-interaction');

  document.getElementById('interaction-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const currentUser = getCurrentUser();

    const intData = {
      client_id: clientId,
      interaction_date: new Date(document.getElementById('int-date').value).toISOString(),
      status: document.getElementById('int-status').value,
      next_followup_date: document.getElementById('int-next-date').value || null,
      notes: document.getElementById('int-notes').value.trim(),
      created_by: currentUser ? currentUser.id : null
    };

    try {
      await addInteraction(intData);
      showToast('Interaction logged successfully!', 'success');
      closeModal('modal-interaction');
      window.location.hash = `#profile?id=${clientId}`;
    } catch (err) {
      showToast(err.message || 'Failed to log interaction', 'error');
    }
  });
}
